import { useMemo } from 'react';
import { FileAudio, Plus, Type, Variable as VariableIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils/cn';
import type { TtsEditorMode, TtsFlowFormValues, TtsRow } from '../../types';
import { TtsFlowPreviewPane } from './TtsFlowPreviewPane';
import { TtsStepCard } from './TtsStepCard';

interface Props {
  values: TtsFlowFormValues;
  setValues: (patch: Partial<TtsFlowFormValues>) => void;
}

function newRow(mode: TtsEditorMode): TtsRow {
  if (mode === 'variable') {
    return { ttsType: 'variable', value: '', variableType: '' };
  }
  if (mode === 'text') {
    return { ttsType: 'prompt', value: '', variableType: '', text: '' };
  }
  return { ttsType: 'prompt', value: '', variableType: '' };
}

export function StepTtsRows({ values, setValues }: Props) {
  // Empty state hides the right pane until at least one row exists.
  const isEmpty = values.ttsRows.length === 0;

  const patchRow = (index: number, patch: Partial<TtsRow>) => {
    setValues({
      ttsRows: values.ttsRows.map((r, i) =>
        i === index ? { ...r, ...patch } : r,
      ),
    });
  };

  const removeRow = (index: number) => {
    setValues({
      ttsRows: values.ttsRows.filter((_, i) => i !== index),
    });
  };

  const moveRow = (index: number, dir: -1 | 1) => {
    const next = [...values.ttsRows];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setValues({ ttsRows: next });
  };

  const addRow = (mode: TtsEditorMode) =>
    setValues({ ttsRows: [...values.ttsRows, newRow(mode)] });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      {/* Left — editable step list */}
      <div className="flex flex-col gap-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>What should the caller hear?</CardTitle>
            <CardDescription>
              Each step plays in order. Mix recorded prompts, inline text
              (auto-synthesized at save), and variable slots filled at call
              time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AddStepRow onAdd={addRow} />
          </CardContent>
        </Card>

        {isEmpty ? (
          <div className="rounded-lg border border-dashed border-border bg-card/50 py-12 text-center">
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <FileAudio className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold text-foreground">
              No steps yet
            </div>
            <p className="mt-1 max-w-sm mx-auto text-xs text-muted-foreground">
              Pick a step type above to start building the flow.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {values.ttsRows.map((row, i) => (
              <TtsStepCard
                key={i}
                row={row}
                index={i}
                totalRows={values.ttsRows.length}
                values={values}
                onPatch={(patch) => patchRow(i, patch)}
                onRemove={() => removeRow(i)}
                onMoveUp={i > 0 ? () => moveRow(i, -1) : undefined}
                onMoveDown={
                  i < values.ttsRows.length - 1
                    ? () => moveRow(i, 1)
                    : undefined
                }
              />
            ))}

            {/* Inline "add another step" footer */}
            <AddStepRow onAdd={addRow} compact />
          </div>
        )}
      </div>

      {/* Right — caller experience preview */}
      <div className="lg:sticky lg:top-2 lg:self-start">
        <TtsFlowPreviewPane
          values={values}
          onPatchRow={(i, patch) => patchRow(i, patch)}
        />
      </div>
    </div>
  );
}

interface AddStepRowProps {
  onAdd: (mode: TtsEditorMode) => void;
  compact?: boolean;
}

/** Three big "+ add step" buttons one per mode. Compact variant collapses
 *  to a single row inside the steps list. */
function AddStepRow({ onAdd, compact }: AddStepRowProps) {
  const OPTIONS = useMemo(
    () => [
      {
        mode: 'prompt' as const,
        label: 'Prompt',
        description: 'Pick an existing audio file from the voice library.',
        icon: FileAudio,
        ring: 'ring-sky-300',
        bg: 'bg-sky-500',
      },
      {
        mode: 'text' as const,
        label: 'Text',
        description: 'Type what the bot says — auto-synthesized at save.',
        icon: Type,
        ring: 'ring-fuchsia-300',
        bg: 'bg-fuchsia-500',
      },
      {
        mode: 'variable' as const,
        label: 'Variable',
        description: 'Speak a runtime-injected value (slot 1–10).',
        icon: VariableIcon,
        ring: 'ring-violet-300',
        bg: 'bg-violet-500',
      },
    ],
    [],
  );

  if (compact) {
    return (
      <div className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-card/50 px-3 py-2">
        <span className="text-[11px] text-muted-foreground">Add</span>
        {OPTIONS.map((opt) => (
          <Button
            key={opt.mode}
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={() => onAdd(opt.mode)}
          >
            <opt.icon className="h-3 w-3" />
            {opt.label}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {OPTIONS.map((opt) => (
        <button
          key={opt.mode}
          type="button"
          onClick={() => onAdd(opt.mode)}
          className={cn(
            'group flex flex-col items-start gap-1.5 rounded-lg border border-border bg-card p-3 text-left transition-all hover:-translate-y-px hover:border-primary/40 hover:shadow-md',
          )}
        >
          <span
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md text-white shadow-sm',
              opt.bg,
            )}
          >
            <opt.icon className="h-4 w-4" />
          </span>
          <span className="flex items-center gap-1 text-sm font-semibold text-foreground">
            <Plus className="h-3 w-3" /> {opt.label}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {opt.description}
          </span>
        </button>
      ))}
    </div>
  );
}
